import React from 'react';

interface LicensePlateProps {
  plate: string;
  size?: 'sm' | 'md' | 'lg' | 'fluid';
  className?: string;
  maxLength?: number; // truncate plate text if longer than this (for unknown/migrated plates)
}

type PlateType = 'mercosul' | 'old' | 'unknown';

function detectPlateType(plate: string): PlateType {
  if (!plate) return 'unknown';
  const p = plate.toUpperCase().replace(/[-\s]/g, '');
  if (/^[A-Z]{3}\d[A-Z]\d{2}$/.test(p)) return 'mercosul';
  if (/^[A-Z]{3}\d{4}$/.test(p)) return 'old';
  return 'unknown';
}

function formatPlate(plate: string, type: PlateType): string {
  const p = plate.toUpperCase().replace(/[-\s]/g, '');
  if (type === 'old' && p.length === 7) {
    return `${p.slice(0, 3)}-${p.slice(3)}`;
  }
  return p;
}

// ── Mini Brazilian flag SVG ────────────────────────────────────────────────────
const BrazilFlag: React.FC<{ w?: number; h?: number }> = ({ w = 14, h = 10 }) => (
  <svg width={w} height={h} viewBox="0 0 14 10" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', flexShrink: 0 }}>
    {/* Green field */}
    <rect width="14" height="10" rx="0.5" fill="#009C3B" />
    {/* Yellow rhombus */}
    <polygon points="7,1 13,5 7,9 1,5" fill="#FFDF00" />
    {/* Blue globe */}
    <circle cx="7" cy="5" r="2.7" fill="#002776" />
    {/* White equatorial band */}
    <path d="M4.6,5.5 Q7,4.0 9.4,5.5" stroke="white" strokeWidth="0.75" fill="none" />
  </svg>
);

// ── Mercosul star cluster (4 five-pointed stars in Mercosur arrangement) ───────
const MercosulStars: React.FC<{ size?: number }> = ({ size = 8 }) => (
  <svg width={size * 2.4} height={size} viewBox="0 0 24 10" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', flexShrink: 0 }}>
    {/* 4 gold stars in 2×2 grid */}
    {[
      [3, 2.5], [12, 2.5], [3, 8], [12, 8],
    ].map(([cx, cy], i) => (
      <text key={i} x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        fontSize="6" fill="#FFDF00">★</text>
    ))}
  </svg>
);

// ── Old-style "BRASIL" strip (green) ─────────────────────────────────────────
const OldStrip: React.FC<{ height: number; fontSize: number }> = ({ height, fontSize }) => (
  <div
    style={{
      backgroundColor: '#1a5c1a',
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    }}
  >
    <span style={{ color: '#FFD700', fontSize, fontWeight: 700, letterSpacing: 1, fontFamily: 'sans-serif' }}>
      BRASIL
    </span>
  </div>
);

// ── Mercosul strip ─────────────────────────────────────────────────────────────
const MercosulStrip: React.FC<{ height: number; flagW: number; flagH: number; starSize: number; fontSize: number }> = ({
  height, flagW, flagH, starSize, fontSize,
}) => (
  <div
    style={{
      backgroundColor: '#003399',
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: 5,
      paddingRight: 5,
    }}
  >
    {/* Left: Mercosul stars */}
    <MercosulStars size={starSize} />

    {/* Center: BRASIL */}
    <span style={{ color: 'white', fontSize, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1, fontFamily: 'sans-serif' }}>
      BRASIL
    </span>

    {/* Right: Brazil flag */}
    <BrazilFlag w={flagW} h={flagH} />
  </div>
);

// ── Unknown strip ──────────────────────────────────────────────────────────────
const UnknownStrip: React.FC<{ height: number; fontSize: number }> = ({ height, fontSize }) => (
  <div
    style={{
      backgroundColor: '#9ca3af',
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <span style={{ color: 'white', fontSize, fontWeight: 700, fontFamily: 'sans-serif' }}>? ? ?</span>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
export const LicensePlate: React.FC<LicensePlateProps> = ({ plate, size = 'sm', className = '', maxLength }) => {
  if (!plate) return null;
  const type = detectPlateType(plate);
  const raw = formatPlate(plate, type);
  const formatted = maxLength && raw.length > maxLength ? raw.slice(0, maxLength) + '…' : raw;

  const borderColor = type === 'mercosul' ? '#003399' : type === 'old' ? '#1a5c1a' : '#9ca3af';
  const textColor = type === 'old' ? '#1a1a1a' : type === 'unknown' ? '#6b7280' : '#111111';

  // ── sm: compact badge ────────────────────────────────────────────────────────
  if (size === 'sm') {
    const stripColor = type === 'mercosul' ? '#003399' : type === 'old' ? '#1a5c1a' : '#9ca3af';
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 3,
          border: `1.5px solid ${borderColor}`,
          fontFamily: 'monospace',
          userSelect: 'none',
          verticalAlign: 'middle',
          alignSelf: 'flex-start',
          width: 'fit-content',
        }}
      >
        {/* thin coloured strip */}
        <span style={{ height: 3, backgroundColor: stripColor, display: 'block' }} />
        {/* plate text */}
        <span
          style={{
            backgroundColor: 'white',
            color: textColor,
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: '0.12em',
            padding: '1px 6px 2px',
            lineHeight: 1.15,
          }}
        >
          {formatted}
        </span>
      </span>
    );
  }

  // ── md and lg: full plate with top strip ─────────────────────────────────────
  if (size === 'md') {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 4,
          border: `2px solid ${borderColor}`,
          fontFamily: 'monospace',
          userSelect: 'none',
          verticalAlign: 'middle',
          alignSelf: 'flex-start',
          width: 'fit-content',
        }}
      >
        {type === 'mercosul' && <MercosulStrip height={18} flagW={12} flagH={8} starSize={7} fontSize={6} />}
        {type === 'old' && <OldStrip height={18} fontSize={7} />}
        {type === 'unknown' && <UnknownStrip height={18} fontSize={7} />}
        <span
          style={{
            backgroundColor: 'white',
            color: textColor,
            fontSize: 16,
            fontWeight: 900,
            letterSpacing: '0.14em',
            padding: '3px 10px 4px',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {formatted}
        </span>
      </span>
    );
  }

  // fluid — fills container width, large text
  if (size === 'fluid') {
    return (
      <span
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 8,
          border: `3px solid ${borderColor}`,
          fontFamily: 'monospace',
          userSelect: 'none',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {type === 'mercosul' && <MercosulStrip height={30} flagW={22} flagH={15} starSize={11} fontSize={9} />}
        {type === 'old' && <OldStrip height={30} fontSize={10} />}
        {type === 'unknown' && <UnknownStrip height={30} fontSize={10} />}
        <span
          style={{
            backgroundColor: 'white',
            color: textColor,
            fontSize: 32,
            fontWeight: 900,
            letterSpacing: '0.18em',
            padding: '8px 12px 10px',
            textAlign: 'center',
            lineHeight: 1.1,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {formatted}
        </span>
      </span>
    );
  }

  // lg
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 6,
        border: `2.5px solid ${borderColor}`,
        fontFamily: 'monospace',
        userSelect: 'none',
        verticalAlign: 'middle',
        alignSelf: 'flex-start',
        width: 'fit-content',
      }}
    >
      {type === 'mercosul' && <MercosulStrip height={26} flagW={18} flagH={13} starSize={10} fontSize={8} />}
      {type === 'old' && <OldStrip height={26} fontSize={9} />}
      {type === 'unknown' && <UnknownStrip height={26} fontSize={9} />}
      <span
        style={{
          backgroundColor: 'white',
          color: textColor,
          fontSize: 22,
          fontWeight: 900,
          letterSpacing: '0.16em',
          padding: '5px 14px 6px',
          textAlign: 'center',
          lineHeight: 1.2,
        }}
      >
        {formatted}
      </span>
    </span>
  );
};

export default LicensePlate;
